import axios from 'axios';

// Validação benchmark data, baked nightly by scripts/bake_validacao.py and
// served from R2 (see docs/prod/VALIDACAO_PLAN.md for the metric definitions).

const DATA_CDN_URL = import.meta.env.VITE_DATA_URL;

export interface ValidacaoCoverage {
  fires_covered: number;
  fires_total: number;
  pct: number | null;
  area_frac_mean: number;
}

export interface ValidacaoVsEntry {
  coverage: number;
  area_frac: number | null;
  area_ratio?: number | null;
}

export interface ValidacaoCalibrationRow {
  band: [number, number];
  n_cell_days: number;
  n_fire_cell_days: number;
  observed_rate: number | null;
}

export interface ValidacaoSkillRow {
  hits: number;
  misses: number;
  false_alarms: number;
  correct_negatives: number;
  tss: number;
  ets: number;
}

export interface ValidacaoScorecards {
  note?: string;
  coverage: { moderate_plus: ValidacaoCoverage; high_plus: ValidacaoCoverage };
  misses: {
    count: number;
    fires_total: number;
    list?: { date: string; concelho: string | null; lat: number; lon: number }[];
  };
  calibration?: Record<string, ValidacaoCalibrationRow>;
  skill: Record<string, ValidacaoSkillRow>;
  vs_ipma: {
    n_days: number;
    n_fires: number;
    from?: string;
    level4_plus?: ValidacaoVsEntry | null;
    level5?: ValidacaoVsEntry | null;
  };
  vs_effis: {
    n_days: number;
    n_fires: number;
    high_plus?: ValidacaoVsEntry | null;
    very_high_plus?: ValidacaoVsEntry | null;
  };
  sweep: { thr: number; area_frac: number; coverage: number }[];
}

export interface ValidacaoDayFires {
  total: number;
  matched: number;
  covered_moderate_plus: number;
  covered_high_plus: number;
  missed_low: number;
  firms_detections: number;
  ge1ha: {
    matched: number;
    covered_moderate_plus: number;
    covered_high_plus: number;
    missed_low: number;
  };
}

export interface ValidacaoDayRecord {
  date: string;
  model_version: string | null;
  n_cells: number;
  flagged_frac: { moderate_plus: number; high_plus: number };
  fires: ValidacaoDayFires;
  rcm: {
    available: boolean;
    level3_plus?: number | null;
    level4_plus?: number | null;
    level5?: number | null;
    max_level?: number | null;
  };
  effis: { available: boolean; mean_fwi?: number; area_frac?: Record<string, number> };
}

// 'probability': the legacy model's bands are probability cutoffs (0.05/0.10/0.20).
// 'share': the rebuilt model and the lookup band by fixed shares of the territory
// (Extreme 1%, High 2%, Moderate 5%), decided by rank each day.
export type BandKind = 'probability' | 'share';

export interface ValidacaoWindow {
  start: string;
  end: string;
  days_evaluated: number;
  missing_prediction_days: string[];
  skipped_days?: string[];
}

// One series per served model family (O5, 2026-10-10): the old model closed on the
// switch day, the rebuilt model from its first served day, the cell-by-month lookup
// of past ignitions as the reference line.
export interface ValidacaoSeries {
  family: string;
  label: string;
  band_kind?: BandKind;
  window: ValidacaoWindow;
  days: ValidacaoDayRecord[];
  scorecards: ValidacaoScorecards;
  scorecards_ge1ha: ValidacaoScorecards;
}

export interface ValidacaoSummary extends ValidacaoSeries {
  generated: string;
  series?: Record<string, ValidacaoSeries>;
  served_series?: string | null;
  switch_date?: string | null;
}

export const SERIES_ORDER = ['offset-booster', 'seasonal-ensemble', 'lookup'];

export interface ValidacaoDayJson {
  date: string;
  model_version: string | null;
  series?: string;
  risk_summary: Record<string, number>;
  cells: [number, number, number, string][]; // [lat, lon, prob, level]
  fires: {
    lat: number;
    lon: number;
    concelho: string | null;
    level: string | null;
    prob: number | null;
  }[];
  firms: [number, number][];
}

export async function getValidacaoSummary(): Promise<ValidacaoSummary> {
  const { data } = await axios.get<ValidacaoSummary>(`${DATA_CDN_URL}/validacao/summary.json`, {
    timeout: 60000,
  });
  return data;
}

export async function getValidacaoDay(date: string): Promise<ValidacaoDayJson> {
  const { data } = await axios.get<ValidacaoDayJson>(
    `${DATA_CDN_URL}/validacao/day-${date}.json`,
    { timeout: 60000 },
  );
  return data;
}
